/**
 * =========================================================================================
 *     DOBLER DATA SOLUTIONS, LLC - PROPRIETARY AND CONFIDENTIAL
 * =========================================================================================
 * The source code contained herein is owned by Dobler Data Solutions, LLC. The source code
 * and all other copyrightable materials related thereto, including without limitation, all
 * text, logos, designs, graphics, other files, and other code, is protected under Title 17
 * of the United States Code. Any use, including any commercial use, distribution,
 * republication, or display of the source code or any other copyrightable materials related
 * thereto is strictly prohibited. Copyright © 2026 Dobler Data Solutions, LLC.
 * ALL RIGHTS RESERVED.
 * =========================================================================================
 *
 *     File Name       :   SwitchBanner.tsx
 *     Description     :   Spec 044 — the header's switches, on every console page. SwitchSummary is the
 *                         collapsed line in the top header row: how many switches are on and off and how
 *                         many items are not built, with the arrow that opens the full view (the
 *                         operator, 2026-10-25). SwitchBanner is that full view, under the title row: each
 *                         switch by its name with its state, the dev test aids as their own group where
 *                         the database says dev, the "not built" items the API lists, a switch missing
 *                         from the store flagged, and "switch state unknown" when the store cannot be
 *                         read (never a switch shown on) (FR-005, FR-006, FR-014).
 *     Module          :   044-visible-feature-switches (frontend/components)
 *     Component Type  :   Component
 *
 * -----------------------------------------------------------------------------------------
 *     CHANGE HISTORY
 * -----------------------------------------------------------------------------------------
 *     Version     Date            Author              Description
 * -----------------------------------------------------------------------------------------
 *     1.0         2026-10-25      Peter Dobler        Initial creation (spec 044 T014, T039)
 *     1.1         2026-10-25      Peter Dobler        Collapsed into the top header (operator: "collaps it into
 *                                                     the top header and list how many switches are on and how
 *                                                     many off and how many not implemented. Then have an
 *                                                     expand arrow"): SwitchSummary added, the full view opens
 *                                                     below it; the phone-only summary removed
 *     1.2         2026-10-25      Peter Dobler        A visible "Switches" label on the summary (operator: "add a
 *                                                     label to the collapsed switches, so we know what it is"); its
 *                                                     icon only below sm, the word from sm
 * -----------------------------------------------------------------------------------------
 *
 *     DEPENDENCIES
 * -----------------------------------------------------------------------------------------
 *     External        :   react react-router-dom lucide-react
 *     Internal        :   api/featureSwitches ui kit feature-switches
 *
 *     EXPORTS
 * -----------------------------------------------------------------------------------------
 *     SwitchBanner SwitchSummary
 *
 * =========================================================================================
 */

import { useId } from "react";
import { Link } from "react-router-dom";
import { Ban, Check, ChevronDown, ChevronUp, CircleAlert, ToggleRight, TriangleAlert, X, type LucideIcon } from "lucide-react";
import {
  FEATURE_SWITCH_DISPLAY_NAMES,
  FEATURE_SWITCH_KEYS,
  type SwitchListResponse,
} from "@nextgenmigration/shared/feature-switches";
import { useSwitches } from "../api/featureSwitches.js";
import { Badge, Spinner, type BadgeVariant } from "./ui/index.js";

type EntryState = "on" | "off" | "missing" | "not-built";

interface Entry {
  readonly key: string;
  readonly name: string;
  readonly state: EntryState;
}

/** The normal state is outlined and quiet; the exceptions (off, missing) are filled so they stand out. */
const CHIP: Readonly<Record<EntryState, { variant: BadgeVariant; outlined: boolean; icon: LucideIcon; label: string }>> = {
  on: { variant: "success", outlined: true, icon: Check, label: "on" },
  off: { variant: "warning", outlined: false, icon: X, label: "off" },
  missing: { variant: "error", outlined: false, icon: CircleAlert, label: "missing, off" },
  "not-built": { variant: "secondary", outlined: true, icon: Ban, label: "not built" },
};

interface Groups {
  readonly switches: readonly Entry[];
  readonly testAids: readonly Entry[];
  readonly notBuilt: readonly Entry[];
}

/**
 * The banner's three groups, in the header's order (the shared key order). A key the store has no row
 * for is a switch read as off, flagged missing; a row this build does not know is shown after the rest.
 * Test aids are shown only where the database says dev (`devTestAidsAllowed`, FR-014).
 */
function groupsOf(list: SwitchListResponse): Groups {
  const known: readonly string[] = FEATURE_SWITCH_KEYS;
  const switches: Entry[] = [];
  const testAids: Entry[] = [];
  const place = (entry: Entry, devOnly: boolean): void => {
    if (!devOnly) switches.push(entry);
    else if (list.devTestAidsAllowed) testAids.push(entry);
  };
  for (const key of FEATURE_SWITCH_KEYS) {
    const row = list.switches.find((s) => s.key === key);
    if (row) place({ key, name: row.displayName, state: row.on ? "on" : "off" }, row.devOnly);
    else switches.push({ key, name: FEATURE_SWITCH_DISPLAY_NAMES[key], state: "missing" });
  }
  for (const row of list.switches) {
    if (!known.includes(row.key)) place({ key: row.key, name: row.displayName, state: row.on ? "on" : "off" }, row.devOnly);
  }
  const notBuilt = list.notBuilt.map((item): Entry => ({ key: item.key, name: item.displayName, state: "not-built" }));
  return { switches, testAids, notBuilt };
}

function Chip({ entry }: { readonly entry: Entry }): React.JSX.Element {
  const chip = CHIP[entry.state];
  const Icon = chip.icon;
  return (
    <li data-testid={`switch-chip-${entry.key}`} data-state={entry.state}>
      <Badge
        variant={chip.variant}
        isOutlined={chip.outlined}
        size="xs"
        leftIcon={<Icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
        className="whitespace-nowrap"
      >
        <span>{entry.name}</span>
        <span className="sr-only">: </span>
        <span className="font-semibold">{chip.label}</span>
      </Badge>
    </li>
  );
}

function Group({ label, entries }: { readonly label: string; readonly entries: readonly Entry[] }): React.JSX.Element | null {
  const id = useId();
  if (entries.length === 0) return null;
  return (
    <div role="group" aria-labelledby={id} className="flex min-w-0 flex-wrap items-center gap-1">
      <span id={id} className="mr-0.5 font-medium text-[var(--color-text-secondary)]">
        {label}
      </span>
      {/* `contents`: the chips flow on the label's line instead of starting a line of their own. */}
      <ul className="contents">
        {entries.map((entry) => (
          <Chip key={entry.key} entry={entry} />
        ))}
      </ul>
    </div>
  );
}

function SwitchesPageLink(): React.JSX.Element {
  return (
    <Link
      to="/switches"
      className="shrink-0 rounded-sm text-[var(--color-accent)] underline-offset-2 hover:text-[var(--color-accent-hover)] hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
    >
      Switches page
    </Link>
  );
}

function FullList({ groups }: { readonly groups: Groups }): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
      <Group label="Switches" entries={groups.switches} />
      <Group label="Dev test aids" entries={groups.testAids} />
      <Group label="Not built" entries={groups.notBuilt} />
      <SwitchesPageLink />
    </div>
  );
}

function Unknown(): React.JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <Badge variant="error" size="xs" leftIcon={<TriangleAlert className="h-3 w-3" aria-hidden="true" />}>
        <span className="font-semibold">Switch state unknown</span>
      </Badge>
      <span>The switch store cannot be read, so every switch is treated as off.</span>
      <SwitchesPageLink />
    </div>
  );
}

function Reading(): React.JSX.Element {
  return (
    <span className="flex items-center gap-1.5 text-[var(--color-text-secondary)]">
      {/* Decorative: the words beside it say what is happening. */}
      <Spinner size="xs" aria-hidden="true" />
      Reading the switches…
    </span>
  );
}

/** The full view (FR-005), under the title row while the summary's arrow has it open. */
export function SwitchBanner({ id }: { readonly id?: string }): React.JSX.Element {
  const switches = useSwitches();

  let content: React.JSX.Element;
  // An error first: an earlier answer is never shown once a read has failed (FR-006).
  if (switches.isError) content = <Unknown />;
  else if (switches.data === undefined) content = <Reading />;
  else content = <FullList groups={groupsOf(switches.data)} />;

  return (
    <div
      id={id}
      role="region"
      aria-label="Feature switches"
      data-testid="switch-banner"
      className="border-t border-[var(--color-border)] px-3 py-1.5 text-xs text-[var(--color-text-primary)] sm:px-6"
    >
      {content}
    </div>
  );
}

type CountState = "on" | "off" | "missing" | "none" | "not-built";

/** As in the full view: the quiet states are outlined; an off or missing switch is filled so it stands out. */
const COUNT: Readonly<Record<CountState, { variant: BadgeVariant; outlined: boolean }>> = {
  on: { variant: "success", outlined: true },
  off: { variant: "warning", outlined: false },
  missing: { variant: "error", outlined: false },
  none: { variant: "secondary", outlined: true },
  "not-built": { variant: "secondary", outlined: true },
};

function Count({ testId, state, icon: Icon, count, word }: {
  readonly testId: string;
  readonly state: CountState;
  readonly icon: LucideIcon;
  readonly count: number;
  readonly word: string;
}): React.JSX.Element {
  return (
    <span data-testid={testId} data-state={state}>
      <Badge
        variant={COUNT[state].variant}
        isOutlined={COUNT[state].outlined}
        size="xs"
        leftIcon={<Icon className="h-3 w-3 shrink-0" aria-hidden="true" />}
        className="whitespace-nowrap tabular-nums"
      >
        {String(count)}
        {/* A phone's title row has room for the icon and the number only (measured at 390 px: the worded
            counts overran the row); the word stays for a screen reader at every width. */}
        <span className="sr-only sm:not-sr-only">{` ${word}`}</span>
      </Badge>
    </span>
  );
}

/**
 * The collapsed line in the top header row (the operator, 2026-10-25): how many switches are on and off
 * (the dev test aids counted with them where the full view shows them; a switch missing from the store counts
 * off) and how many items are not built, as the button whose arrow opens and closes the full view.
 */
export function SwitchSummary({ expanded, onToggle, panelId }: {
  readonly expanded: boolean;
  readonly onToggle: () => void;
  readonly panelId: string;
}): React.JSX.Element {
  const switches = useSwitches();
  const action = expanded ? "Hide the switches" : "Show the switches";
  const Chevron = expanded ? ChevronUp : ChevronDown;

  let body: React.JSX.Element;
  // As in the full view: an error first, and no count while the store has not answered (FR-006).
  if (switches.isError)
    body = (
      <Badge variant="error" size="xs" leftIcon={<TriangleAlert className="h-3 w-3" aria-hidden="true" />}>
        <span className="font-semibold">Switch state unknown</span>
      </Badge>
    );
  else if (switches.data === undefined) body = <Reading />;
  else {
    const groups = groupsOf(switches.data);
    const shown = [...groups.switches, ...groups.testAids];
    const on = shown.filter((e) => e.state === "on").length;
    const off = shown.length - on;
    const offState: CountState = shown.some((e) => e.state === "missing") ? "missing" : off > 0 ? "off" : "none";
    body = (
      <>
        <Count testId="switch-count-on" state="on" icon={Check} count={on} word="on" />
        <Count
          testId="switch-count-off"
          state={offState}
          icon={offState === "missing" ? CircleAlert : X}
          count={off}
          word="off"
        />
        <Count testId="switch-count-not-built" state="not-built" icon={Ban} count={groups.notBuilt.length} word="not built" />
      </>
    );
  }

  return (
    <button
      type="button"
      aria-expanded={expanded}
      aria-controls={panelId}
      title={action}
      onClick={onToggle}
      data-testid="switch-summary"
      className="inline-flex min-w-0 items-center gap-1 rounded-md px-1 py-1 text-xs sm:px-1.5 text-[var(--color-text-primary)] hover:bg-[var(--color-bg-subtle)] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-accent)]"
    >
      <span className="sr-only">{`${action}: `}</span>
      {/* The visible label (the operator, 2026-10-25: "add a label to the collapsed switches, so we know what it
          is"). A phone's title row has room for its icon only (measured at 390 px); the word shows from sm. Hidden
          from assistive tech: the button's name already says "the switches". */}
      <span
        data-testid="switch-summary-label"
        aria-hidden="true"
        className="mr-0.5 inline-flex items-center gap-1 font-medium text-[var(--color-text-secondary)]"
      >
        <ToggleRight className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden sm:inline">Switches</span>
      </span>
      {body}
      <Chevron className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" aria-hidden="true" />
    </button>
  );
}
